// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.DashboardDetailFooterActions.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import PropTypes from 'prop-types';

import { curry, isEmpty } from 'lodash/fp';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import { EMAIL_TYPE } from 'logic/data/Izenda.EmailConstants';
import { compose, setPropTypes, withHandlers } from 'recompose';
import EmailPopup from 'share/components/controls/CommonPopup/Izenda.EmailPopup';
import ActionPopup from 'share/components/controls/SavePopup/Izenda.ActionPopup';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import RouteUtils from 'share/utils/Izenda.RouteUtils';

const DashboardDetailFooterActions = compose(
  withHandlers({
    deleteDashboard: curry(deleteDashboard),
    openEmailPopup: curry(openEmailPopup),
    handlePrint: curry(handlePrint),
    moveDashboard: curry(moveDashboard),
    copyDashboard: curry(copyDashboard),
    subscribeDashboard: curry(subscribeDashboard)
  }),
  setPropTypes({
    store: PropTypes.object,
    boardDetail: PropTypes.object
  })
);

/**
 * Handle action when click delete dashboard button
 */
function deleteDashboard(props: any, e): void {
  const { store, loader, subCategory, boardDetail } = props;
  const message = DashboardConstants.MESSAGE.M01;

  props
    .confirmFn('Confirmation', {
      description: String.format(message, boardDetail.name)
    })
    .then(() => {
      loader.startLoading();
      return store.deleteDashboard(boardDetail.id, subCategory);
    })
    .then(result => {
      !isEmpty(result) && props.router.push(RouteUtils.getDashboardUrl());
    })
    .finally(loader.endLoading.bind(loader));
}

/**
 * Handle subscribe of dashboard.
 * @param {Object} props
 * @param {Object} e
 */
function subscribeDashboard(props: any, e): void {
  const boardDetail = props.boardDetail || {};
  const {
    store: {
      model: {
        dashboardDetail: { dashboard },
        dashboardDesignerMode
      }
    }
  } = props;
  dashboard.id = boardDetail.id;
  dashboard.subscriptions.clear();

  dashboardDesignerMode.switchSubscribeMode(true);
}

/**
 * Open Email Popup
 */
function openEmailPopup(props: any, _): void {
  const { boardDetail, store } = props;
  const handleCloseEmailPopup = email => {
    return email && store.sendEmail(boardDetail.id, email);
  };

  props.openPopup(EmailPopup, {
    onClosed: handleCloseEmailPopup,
    type: EMAIL_TYPE.dashboard,
    dashboardId: boardDetail.id
  });
}

/**
 * Print Report
 */
function handlePrint(props: any, e): void {
  const {
    boardDetail: { id }
  } = props;
  const url = RouteUtils.getViewerUrl('Dashboard', { id: id });

  BrowserUtils.openNewTab(RouteUtils.makeAbsoluteUrl(url, 'print=true'));
}

/**
 * Handle when click move button
 */
function moveDashboard(props: any, e): void {
  openActionPopup(props, DashboardConstants.MOVE_KEY);
}

/**
 * Handle when click copy button
 */
function copyDashboard(props: any, e): void {
  openActionPopup(props, DashboardConstants.COPY_KEY);
}

/**
 * Open Action Popup
 */
function openActionPopup(props: any, action: string) {
  const { store, boardDetail = {}, loader, authorization, authorizationConstants } = props;
  const model = store.model;

  /**
   * Get action function base on move or copy
   * @param  {Object} data dashboard data
   * @return {Object}      Promise
   */
  const getActionFunc = data => {
    const functionCall = action === DashboardConstants.COPY_KEY ? store.copyDashboard : store.moveDashboard;
    return functionCall.bind(store)(data);
  };

  const permissionKeys = authorizationConstants.PERMISSION_KEY;

  props
    .openPopup(ActionPopup, {
      action,
      actionFunc: getActionFunc,
      dashboard: boardDetail,
      getCategoriesFunc: store.lazyLoadCategories,
      getSubcategoriesFunc: store.lazyLoadSubCategories,
      popupConfig: DashboardConstants.SAVE_POPUP_CONFIG,
      hasCanCreateNewCategoryPrivilege: authorization.checkPrivileges([
        permissionKeys.DashboardsCanCreateNewCategoryValue
      ])
    })
    .then(message => {
      return props.confirmMessage('info', message);
    })
    .then(() => {
      loader.startLoading();
      // reload dashboards panel
      // reload category left panel
      return Promise.all([
        store.lazyLoadingDashboards(store.selectedCategoryId, true),
        store.loadLazyLoadingCategories(true)
      ]);
    })
    .then(() => {
      model.items.isEmpty && props.router.push(RouteUtils.getDashboardUrl());
    })
    .finally(loader.endLoading.bind(loader));
}

export default DashboardDetailFooterActions;



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardList/Izenda.DashboardDetailFooterActions.js